import { z } from 'zod';

export interface ReceptionInspectionStatusOption {
  readonly value: string;
  readonly label: string;
}

/** Stable lighting corner keys (BR-SO-195). */
export interface ReceptionInspectionPositionOption {
  readonly value: string;
  readonly label: string;
}

const POSITION_FRONT_LEFT = {
  value: 'front-left',
  label: 'Dianteiro esquerdo',
} as const satisfies ReceptionInspectionPositionOption;

const POSITION_FRONT_RIGHT = {
  value: 'front-right',
  label: 'Dianteiro direito',
} as const satisfies ReceptionInspectionPositionOption;

const POSITION_REAR_LEFT = {
  value: 'rear-left',
  label: 'Traseiro esquerdo',
} as const satisfies ReceptionInspectionPositionOption;

const POSITION_REAR_RIGHT = {
  value: 'rear-right',
  label: 'Traseiro direito',
} as const satisfies ReceptionInspectionPositionOption;

const LIGHTING_FRONT_POSITIONS = [POSITION_FRONT_LEFT, POSITION_FRONT_RIGHT] as const;
const LIGHTING_REAR_POSITIONS = [POSITION_REAR_LEFT, POSITION_REAR_RIGHT] as const;
const LIGHTING_ALL_POSITIONS = [
  POSITION_FRONT_LEFT,
  POSITION_FRONT_RIGHT,
  POSITION_REAR_LEFT,
  POSITION_REAR_RIGHT,
] as const;

/** BR-SO-164: OK / Meia vida / Careca — pneus e estepe. */
const TIRE_CONDITION_OPTIONS = [
  { value: 'OK', label: 'OK' },
  { value: 'HALF_LIFE', label: 'Meia vida' },
  { value: 'BALD', label: 'Careca' },
] as const satisfies readonly ReceptionInspectionStatusOption[];

/** BR-SO-164: OK / Queimado — uniforme para toda a categoria Iluminação. */
const LIGHTING_OPTIONS = [
  { value: 'OK', label: 'OK' },
  { value: 'BURNED_OUT', label: 'Queimado' },
] as const satisfies readonly ReceptionInspectionStatusOption[];

/** BR-SO-164: OK / Avaria — itens estruturais do interior (sem "Atenção"). */
const INTERIOR_STRUCTURAL_OPTIONS = [
  { value: 'OK', label: 'OK' },
  { value: 'DAMAGE', label: 'Avaria' },
] as const satisfies readonly ReceptionInspectionStatusOption[];

/** BR-SO-164: OK / Não funciona — itens funcionais do interior. */
const INTERIOR_FUNCTIONAL_OPTIONS = [
  { value: 'OK', label: 'OK' },
  { value: 'NOT_WORKING', label: 'Não funciona' },
] as const satisfies readonly ReceptionInspectionStatusOption[];

/** BR-SO-164: OK / Atenção — sem "Avaria" para fluidos. */
const FLUID_OPTIONS = [
  { value: 'OK', label: 'OK' },
  { value: 'ATTENTION', label: 'Atenção' },
] as const satisfies readonly ReceptionInspectionStatusOption[];

export type ReceptionInspectionCatalogItem =
  | {
      readonly itemKey: string;
      readonly label: string;
      readonly kind: 'status';
      readonly statusOptions: readonly ReceptionInspectionStatusOption[];
      readonly allowedPositions?: readonly ReceptionInspectionPositionOption[];
    }
  | {
      readonly itemKey: string;
      readonly label: string;
      readonly kind: 'quantity';
      readonly quantityMin: number;
      readonly quantityMax: number;
    };

export interface ReceptionInspectionCatalogCategory {
  readonly categoryKey: string;
  readonly label: string;
  readonly items: readonly ReceptionInspectionCatalogItem[];
}

/** BR-SO-164: por categoria/item — não é mais um único enum uniforme de 3 valores. */
export const RECEPTION_INSPECTION_CHECKLIST_CATALOG = [
  {
    categoryKey: 'tires-wheels',
    label: 'Pneus & Rodas',
    items: [
      {
        itemKey: 'front-left',
        label: 'Dianteiro esq.',
        kind: 'status',
        statusOptions: TIRE_CONDITION_OPTIONS,
      },
      {
        itemKey: 'front-right',
        label: 'Dianteiro dir.',
        kind: 'status',
        statusOptions: TIRE_CONDITION_OPTIONS,
      },
      {
        itemKey: 'rear-left',
        label: 'Traseiro esq.',
        kind: 'status',
        statusOptions: TIRE_CONDITION_OPTIONS,
      },
      {
        itemKey: 'rear-right',
        label: 'Traseiro dir.',
        kind: 'status',
        statusOptions: TIRE_CONDITION_OPTIONS,
      },
      {
        itemKey: 'spare-tire',
        label: 'Estepe',
        kind: 'status',
        statusOptions: TIRE_CONDITION_OPTIONS,
      },
      {
        itemKey: 'wheel-covers',
        label: 'Calotas',
        kind: 'quantity',
        quantityMin: 0,
        quantityMax: 4,
      },
    ],
  },
  {
    categoryKey: 'lighting',
    label: 'Iluminação',
    items: [
      {
        itemKey: 'headlights',
        label: 'Faróis',
        kind: 'status',
        statusOptions: LIGHTING_OPTIONS,
        allowedPositions: LIGHTING_FRONT_POSITIONS,
      },
      {
        itemKey: 'tail-lights',
        label: 'Lanternas',
        kind: 'status',
        statusOptions: LIGHTING_OPTIONS,
        allowedPositions: LIGHTING_REAR_POSITIONS,
      },
      {
        itemKey: 'turn-signals',
        label: 'Setas',
        kind: 'status',
        statusOptions: LIGHTING_OPTIONS,
        allowedPositions: LIGHTING_ALL_POSITIONS,
      },
      {
        itemKey: 'brake-lights',
        label: 'Luz de freio',
        kind: 'status',
        statusOptions: LIGHTING_OPTIONS,
        allowedPositions: LIGHTING_REAR_POSITIONS,
      },
      {
        itemKey: 'reverse-lights',
        label: 'Luz de ré',
        kind: 'status',
        statusOptions: LIGHTING_OPTIONS,
        allowedPositions: LIGHTING_REAR_POSITIONS,
      },
    ],
  },
  {
    categoryKey: 'interior',
    label: 'Interior',
    items: [
      {
        itemKey: 'seats',
        label: 'Bancos',
        kind: 'status',
        statusOptions: INTERIOR_STRUCTURAL_OPTIONS,
      },
      {
        itemKey: 'dashboard',
        label: 'Painel',
        kind: 'status',
        statusOptions: INTERIOR_STRUCTURAL_OPTIONS,
      },
      {
        itemKey: 'steering-wheel',
        label: 'Volante',
        kind: 'status',
        statusOptions: INTERIOR_STRUCTURAL_OPTIONS,
      },
      {
        itemKey: 'air-conditioning',
        label: 'Ar-condicionado',
        kind: 'status',
        statusOptions: INTERIOR_FUNCTIONAL_OPTIONS,
      },
      {
        itemKey: 'power-windows',
        label: 'Vidros elétricos',
        kind: 'status',
        statusOptions: INTERIOR_FUNCTIONAL_OPTIONS,
      },
    ],
  },
  {
    categoryKey: 'fluids',
    label: 'Fluidos',
    items: [
      {
        itemKey: 'engine-oil',
        label: 'Óleo do motor',
        kind: 'status',
        statusOptions: FLUID_OPTIONS,
      },
      {
        itemKey: 'coolant',
        label: 'Água do radiador',
        kind: 'status',
        statusOptions: FLUID_OPTIONS,
      },
      {
        itemKey: 'brake-fluid',
        label: 'Fluido de freio',
        kind: 'status',
        statusOptions: FLUID_OPTIONS,
      },
      {
        itemKey: 'washer-fluid',
        label: 'Líquido limpador',
        kind: 'status',
        statusOptions: FLUID_OPTIONS,
      },
    ],
  },
] as const satisfies readonly ReceptionInspectionCatalogCategory[];

export function findReceptionInspectionCatalogItem(
  categoryKey: string,
  itemKey: string,
): ReceptionInspectionCatalogItem | undefined {
  const category = RECEPTION_INSPECTION_CHECKLIST_CATALOG.find(
    (c) => c.categoryKey === categoryKey,
  );
  return category?.items.find((item) => item.itemKey === itemKey);
}

/**
 * Tolerant wire schema (BR-SO-164): `status`/`quantity` accepted loosely so historical
 * snapshots (old uniform enum, removed "bodywork" category) still deserialize without
 * throwing. Strict per-item validation against the current catalog happens in the domain
 * layer, only for freshly submitted checklists.
 */
export const ReceptionInspectionChecklistItemSchema = z.object({
  categoryKey: z.string().trim().min(1),
  itemKey: z.string().trim().min(1),
  status: z.string().trim().min(1).nullable().optional(),
  quantity: z.number().int().min(0).max(99).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  /** BR-SO-195: lighting positions — tolerant on read; domain enforces on write. */
  positions: z.array(z.string().trim().min(1)).optional(),
});

export type ReceptionInspectionChecklistItem = z.infer<
  typeof ReceptionInspectionChecklistItemSchema
>;
